import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  DEFAULT_VARIANT_COUNTS,
  VARIANT_KIND_LABELS,
  SERVICE_LINK_KIND_LABELS,
  VEHICLE_CLASSES,
  VEHICLE_CLASS_LABELS,
  parseServiceVariant,
  serviceBaseName,
  sortVariants,
  variantAxis,
  variantLabel,
  variantLabelFor,
  type CategoryWithServices,
  type Product,
  type PricingUnit,
  type Service,
  type ServiceVariantGroup,
  type VariantKind,
  type VehicleClass,
} from "@shared/modules/x-atelier";
import { moduleEnabled } from "../../../core/blueprint";
import { api } from "../../../core/lib/api";
import { useToastStore } from "../../../core/stores/toast";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input, Select, Textarea } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import {
  getServiceProducts,
  type ServiceInput,
  type ServiceLinkItemInput,
  type ServiceProductInput,
} from "../lib/api";
import { centsToInput, parseAmountToCents } from "../lib/money";
import { ServiceGuidesPicker } from "./service-guides";
import {
  ServiceProductsEditor,
  draftFromServiceProduct,
  serviceProductsToInput,
  type DraftServiceProduct,
} from "./service-products";
import { VehicleIcon } from "./vehicle-icon";

// Un service et ses déclinaisons se créent et se modifient dans une seule
// fenêtre : le nom, la description et les options communes en haut, puis la
// tarification — un seul prix, ou une grille par type de véhicule, par nombre
// de sièges ou de tapis, ou des déclinaisons libres. Chaque ligne de la grille
// devient un service du catalogue (« Antirouille annuel — VUS »), rattaché aux
// autres par le même groupe de variantes : la base ne change pas de forme.
//
// Une déclinaison seule (clic sur sa pastille) s'ouvre dans la même fenêtre,
// réduite à ce qui lui est propre : libellé, prix, durée, main-d'œuvre,
// produits consommés, actif.

const FORM_ID = "atelier-service-form";

export type PricingAxis = "single" | VariantKind;

const AXIS_LABELS: Record<PricingAxis, string> = {
  single: "Un seul prix",
  ...VARIANT_KIND_LABELS,
};

const AXES: PricingAxis[] = ["single", "vehicle", "seats", "carpets", "custom"];

/** Champs communs à toutes les déclinaisons d'un service. */
export interface SharedFields {
  name: string;
  description: string | null;
  quoteOnly: boolean;
  onlineBookable: boolean;
  publicVisible: boolean;
  aiPriceVisible: boolean;
  /** Absent : l'état actif de chaque déclinaison est conservé. */
  active?: boolean;
  guideSlugs: string[];
  bundleServiceIds: string[];
  warrantyEligible: boolean;
  /** Classes visées quand l'axe n'est pas le véhicule (vide = tous). */
  vehicleClasses: VehicleClass[];
  /** « hourly » : le prix est le taux d'une heure, la durée sert d'estimation. */
  pricingUnit: PricingUnit;
  /** Réservé aux véhicules de tant d'années ou moins ; null = sans limite. */
  maxVehicleAgeYears: number | null;
  /** Transport : rayon maximal en kilomètres ; null = pas un transport. */
  maxDistanceKm: number | null;
  /** Services liés : ajoutés d'office (obligatoire) ou proposés (recommandé). */
  links: ServiceLinkItemInput[];
}

export interface PlanRow {
  /** Service existant à mettre à jour ; absent = à créer. */
  id?: string;
  /** Suffixe de déclinaison ; vide pour un service à prix unique. */
  label: string;
  /** Axe véhicule : la classe de la ligne ; sinon null (les classes communes s'appliquent). */
  vehicleClasses: VehicleClass[] | null;
  priceCents: number | null;
  durationMin: number | null;
  laborMin: number | null;
}

export type ServicePlan =
  | {
      kind: "group";
      categoryId: string;
      variantGroup: string | null;
      shared: SharedFields;
      rows: PlanRow[];
      deleteIds: string[];
      /** Produits posés sur chaque ligne ; null = chaque déclinaison garde les siens. */
      products: ServiceProductInput[] | null;
    }
  | {
      kind: "variant";
      id: string;
      input: Partial<ServiceInput>;
      products: ServiceProductInput[];
    };

interface RowDraft {
  key: string;
  id?: string;
  label: string;
  vehicleClass?: VehicleClass;
  count?: number;
  extra?: boolean;
  /** Ligne d'une grille fixe (véhicule, quantités) : le libellé n'est pas modifiable. */
  fixed: boolean;
  included: boolean;
  price: string;
  durationMin: number | null;
  laborMin: string;
  /** Nom actuel de la déclinaison existante, pour l'avertissement de suppression. */
  currentName?: string;
}

interface Draft {
  categoryId: string;
  name: string;
  description: string;
  quoteOnly: boolean;
  onlineBookable: boolean;
  publicVisible: boolean;
  aiPriceVisible: boolean;
  active: boolean;
  activeTouched: boolean;
  guideSlugs: string[];
  bundleServiceIds: string[];
  warrantyEligible: boolean;
  vehicleClasses: VehicleClass[];
  axis: PricingAxis;
  rows: RowDraft[];
  pricingUnit: PricingUnit;
  maxVehicleAgeYears: string;
  maxDistanceKm: string;
  links: ServiceLinkItemInput[];
}

let counter = 0;
const nextKey = () => `row${Date.now().toString(36)}-${++counter}`;

/** Clé de groupe d'un nouveau service décliné (aucune signification : juste unique). */
function newGroupKey(): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 12)
      : `${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
  return `grp_${random}`;
}

const laborToInput = (laborMin: number | null | undefined) =>
  laborMin !== null && laborMin !== undefined ? String(laborMin) : "";

function rowFromService(s: Service, fixed: boolean, template?: Partial<RowDraft>): RowDraft {
  return {
    key: nextKey(),
    id: s.id,
    label: variantLabel(s),
    fixed,
    included: true,
    price: centsToInput(s.priceCents),
    durationMin: s.durationMin,
    laborMin: laborToInput(s.laborMin),
    currentName: s.name,
    ...template,
  };
}

function emptyRow(template: Partial<RowDraft> & { label: string }): RowDraft {
  return {
    key: nextKey(),
    fixed: false,
    included: false,
    price: "",
    durationMin: 60,
    laborMin: "",
    ...template,
  };
}

/**
 * Lignes d'une grille pour un axe donné, remplies avec les déclinaisons qui
 * existent. Une déclinaison qui n'entre pas dans la grille (« Sedan/Vus »,
 * « Sièges et tapis ») reste une ligne libre : rien ne disparaît en changeant
 * d'axe, sauf ce que la fenêtre annonce explicitement.
 */
function buildRows(axis: PricingAxis, variants: Service[]): RowDraft[] {
  const sorted = sortVariants(variants);
  if (axis === "single") {
    const first = sorted[0];
    return [
      first
        ? rowFromService(first, true, { label: "" })
        : emptyRow({ label: "", fixed: true, included: true }),
    ];
  }
  const leftovers = [...sorted];
  const take = (pick: (s: Service) => boolean): Service | undefined => {
    const index = leftovers.findIndex(pick);
    if (index < 0) return undefined;
    return leftovers.splice(index, 1)[0];
  };
  const rows: RowDraft[] = [];
  if (axis === "vehicle") {
    for (const vehicleClass of VEHICLE_CLASSES) {
      const existing = take((s) => {
        const p = parseServiceVariant(s);
        return (
          p.kind === "vehicle" &&
          p.vehicleClasses.length === 1 &&
          p.vehicleClasses[0] === vehicleClass
        );
      });
      rows.push(
        existing
          ? rowFromService(existing, true, { vehicleClass })
          : emptyRow({
              label: variantLabelFor("vehicle", vehicleClass),
              vehicleClass,
              fixed: true,
              included: ["sedan", "suv", "truck"].includes(vehicleClass) && variants.length === 0,
            }),
      );
    }
  } else if (axis === "seats" || axis === "carpets") {
    const counts = new Set<number>(DEFAULT_VARIANT_COUNTS);
    for (const s of sorted) {
      const p = parseServiceVariant(s);
      if (p.kind === axis && !p.extra && p.count !== null) counts.add(p.count);
    }
    for (const count of [...counts].sort((a, b) => a - b)) {
      const existing = take((s) => {
        const p = parseServiceVariant(s);
        return p.kind === axis && !p.extra && p.count === count;
      });
      rows.push(
        existing
          ? rowFromService(existing, true, { count })
          : emptyRow({
              label: variantLabelFor(axis, count),
              count,
              fixed: true,
              // Nouveau service : les quantités courantes sont cochées d'office.
              included: variants.length === 0,
            }),
      );
    }
    const extra = take((s) => {
      const p = parseServiceVariant(s);
      return p.kind === axis && p.extra;
    });
    rows.push(
      extra
        ? rowFromService(extra, true, { count: 1, extra: true })
        : emptyRow({ label: variantLabelFor(axis, 1, true), count: 1, extra: true, fixed: true }),
    );
  }
  for (const s of leftovers) rows.push(rowFromService(s, false));
  if (axis === "custom" && rows.length === 0) rows.push(emptyRow({ label: "", included: true }));
  return rows;
}

function draftFrom(categoryId: string, group?: ServiceVariantGroup): Draft {
  const variants = group ? sortVariants(group.variants) : [];
  const first = variants[0];
  const axis: PricingAxis = !group
    ? "single"
    : variants.length > 1
      ? variantAxis(variants)
      : "single";
  return {
    categoryId: first?.categoryId ?? categoryId,
    name: group?.name ?? "",
    description: sharedDescription(variants) ?? "",
    quoteOnly: first?.quoteOnly ?? false,
    onlineBookable: first?.onlineBookable ?? true,
    publicVisible: first?.publicVisible ?? true,
    aiPriceVisible: first?.aiPriceVisible ?? true,
    active: variants.length ? variants.every((v) => v.active) : true,
    activeTouched: false,
    guideSlugs: first?.guideSlugs ?? [],
    bundleServiceIds: first?.bundleServiceIds ?? [],
    warrantyEligible: first?.warrantyEligible ?? false,
    vehicleClasses: axis === "vehicle" ? [] : (first?.vehicleClasses ?? []),
    axis,
    rows: buildRows(axis, variants),
    pricingUnit: first?.pricingUnit ?? "fixed",
    maxVehicleAgeYears:
      first?.maxVehicleAgeYears !== null && first?.maxVehicleAgeYears !== undefined
        ? String(first.maxVehicleAgeYears)
        : "",
    maxDistanceKm:
      first?.maxDistanceKm !== null && first?.maxDistanceKm !== undefined
        ? String(first.maxDistanceKm)
        : "",
    links: (first?.links ?? []).map((l) => ({
      linkedServiceId: l.linkedServiceId,
      kind: l.kind,
      note: l.note,
    })),
  };
}

/** Description commune, sinon la première renseignée. */
function sharedDescription(variants: Service[]): string | null {
  const described = variants.map((v) => v.description?.trim() ?? "").filter(Boolean);
  if (described.length === 0) return null;
  return described.every((d) => d === described[0]) ? described[0]! : described[0]!;
}

/** Déclinaisons existantes qui ne survivront pas à l'enregistrement. */
function pendingDeletions(draft: Draft, variants: Service[]): Service[] {
  const kept = new Set(draft.rows.filter((r) => r.included && r.id).map((r) => r.id!));
  return variants.filter((v) => !kept.has(v.id));
}

// ---- Rappels de suivi (module messagerie, par HTTP seulement) ----

interface FollowupHit {
  id: string;
  label: string;
  delayValue: number;
  delayUnit: "day" | "week" | "month" | "year";
  repeatCount: number;
  enabled: boolean;
}

const UNIT_LABELS: Record<FollowupHit["delayUnit"], [string, string]> = {
  day: ["jour", "jours"],
  week: ["semaine", "semaines"],
  month: ["mois", "mois"],
  year: ["an", "ans"],
};

/**
 * Rappels de suivi rattachés au service (« inspection aux deux ans ») : lus
 * et gérés dans la messagerie, rappelés ici parce que c'est en créant le
 * service qu'on y pense.
 */
function FollowupsHint({ serviceId }: { serviceId: string | null }) {
  const [hits, setHits] = useState<FollowupHit[] | null>(null);
  const enabled = moduleEnabled("x-messagerie");
  useEffect(() => {
    if (!enabled || !serviceId) {
      setHits(null);
      return;
    }
    let cancelled = false;
    api<FollowupHit[]>(`/api/messagerie/followups?serviceId=${encodeURIComponent(serviceId)}`)
      .then((list) => {
        if (!cancelled) setHits(list);
      })
      .catch(() => {
        if (!cancelled) setHits([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, serviceId]);
  if (!enabled) return null;
  const manage = serviceId
    ? `/messages/rappels?service=${encodeURIComponent(serviceId)}`
    : "/messages/rappels";
  return (
    <fieldset className="atelier-subsection">
      <legend className="atelier-subsection__title">Rappels de suivi</legend>
      {!serviceId ? (
        <p className="atelier-subsection__empty">
          Enregistrez d'abord le service ; vous pourrez ensuite lui ajouter un rappel (ex. :
          inspection aux deux ans) dans <Link to={manage}>Messagerie › Rappels</Link>.
        </p>
      ) : hits === null ? (
        <p className="atelier-subsection__empty">Chargement…</p>
      ) : hits.length === 0 ? (
        <p className="atelier-subsection__empty">
          Aucun rappel après ce service. <Link to={manage}>Ajouter un rappel</Link>
        </p>
      ) : (
        <ul className="service-followups">
          {hits.map((h) => {
            const [one, many] = UNIT_LABELS[h.delayUnit];
            const delay = `${new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 2 }).format(h.delayValue)} ${h.delayValue > 1 ? many : one}`;
            return (
              <li key={h.id} className={h.enabled ? "" : "is-off"}>
                <strong>{h.label}</strong> — {delay} après le travail
                {h.repeatCount > 0 ? `, ×${h.repeatCount + 1}` : ""}
                {h.enabled ? "" : " (désactivé)"}
              </li>
            );
          })}
          <li>
            <Link to={manage}>Gérer les rappels</Link>
          </li>
        </ul>
      )}
    </fieldset>
  );
}

// ---- Fenêtre ----

interface ServiceEditorProps {
  open: boolean;
  categories: CategoryWithServices[];
  products: Product[];
  /** Catégorie d'un nouveau service. */
  categoryId: string;
  /** Service (et ses déclinaisons) en modification ; absent = création. */
  group?: ServiceVariantGroup;
  /** Une déclinaison seule (clic sur sa pastille). */
  variant?: Service;
  busy: boolean;
  onClose: () => void;
  onSave: (plan: ServicePlan) => Promise<boolean>;
}

export function ServiceEditor(props: ServiceEditorProps) {
  if (!props.open) return null;
  return props.variant && props.group ? (
    <VariantEditor {...props} variant={props.variant} group={props.group} />
  ) : (
    <GroupEditor {...props} />
  );
}

function GroupEditor({
  categories,
  products,
  categoryId,
  group,
  busy,
  onClose,
  onSave,
}: ServiceEditorProps) {
  const push = useToastStore((s) => s.push);
  const variants = useMemo(() => (group ? sortVariants(group.variants) : []), [group]);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(categoryId, group));
  // Produits : posés sur chaque déclinaison à la création ; en modification,
  // chacune garde les siens (modifiables depuis sa pastille).
  const creating = variants.length === 0;
  const [consumed, setConsumed] = useState<DraftServiceProduct[]>([]);
  useEffect(() => {
    if (variants.length === 1) {
      getServiceProducts(variants[0]!.id)
        .then((list) => setConsumed(list.map(draftFromServiceProduct)))
        .catch(() => setConsumed([]));
    }
  }, [variants]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const setRow = (key: string, patch: Partial<RowDraft>) =>
    setDraft((d) => ({ ...d, rows: d.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) }));

  const chooseAxis = (axis: PricingAxis) =>
    setDraft((d) => ({
      ...d,
      axis,
      rows: buildRows(axis, variants),
      vehicleClasses: axis === "vehicle" ? [] : d.vehicleClasses,
    }));

  const addCountRow = () => {
    const taken = new Set(draft.rows.map((r) => r.count).filter((c) => c !== undefined));
    let count = 1;
    while (taken.has(count)) count++;
    const axis = draft.axis as "seats" | "carpets";
    setDraft((d) => {
      const rows = [
        ...d.rows,
        emptyRow({ label: variantLabelFor(axis, count), count, fixed: true, included: true }),
      ];
      rows.sort((a, b) => rankOf(a) - rankOf(b));
      return { ...d, rows };
    });
  };
  const addCustomRow = () =>
    setDraft((d) => ({ ...d, rows: [...d.rows, emptyRow({ label: "", included: true })] }));
  const removeRow = (key: string) =>
    setDraft((d) => ({
      ...d,
      rows: d.rows.flatMap((r) => (r.key !== key ? [r] : r.id ? [{ ...r, included: false }] : [])),
    }));

  const deletions = pendingDeletions(draft, variants);
  const single = draft.axis === "single";
  const includedRows = draft.rows.filter((r) => r.included);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const name = draft.name.trim();
    if (!name || !draft.categoryId) return;
    if (includedRows.length === 0) {
      push("danger", "Aucune déclinaison", "Cochez au moins une ligne de la grille.");
      return;
    }
    const rows: PlanRow[] = [];
    for (const r of includedRows) {
      const label = single ? "" : r.label.trim();
      if (!single && !label) {
        push("danger", "Libellé manquant", "Chaque déclinaison a besoin d'un libellé.");
        return;
      }
      const priceCents = draft.quoteOnly ? null : parseAmountToCents(r.price);
      if (!draft.quoteOnly && priceCents === null) {
        push(
          "danger",
          "Prix illisible",
          `${label || name} : saisissez un montant, par exemple 189,00.`,
        );
        return;
      }
      const laborRaw = r.laborMin.trim();
      const laborMin = laborRaw === "" ? null : Math.round(Number(laborRaw));
      if (laborMin !== null && (!Number.isFinite(laborMin) || laborMin < 0)) {
        push("danger", "Main-d'œuvre illisible", "Un nombre de minutes, ou vide.");
        return;
      }
      rows.push({
        id: r.id,
        label,
        vehicleClasses: draft.axis === "vehicle" && r.vehicleClass ? [r.vehicleClass] : null,
        priceCents,
        durationMin: r.durationMin === null ? null : Math.max(5, Math.round(r.durationMin) || 5),
        laborMin,
      });
    }
    const items = creating || variants.length === 1 ? serviceProductsToInput(consumed) : null;
    if ((creating || variants.length === 1) && !items) {
      push(
        "danger",
        "Produits incomplets",
        "Chaque ligne doit avoir un produit (sans doublon) et une quantité positive.",
      );
      return;
    }
    const limit = (raw: string, label: string, max: number): number | null | false => {
      const text = raw.trim();
      if (text === "") return null;
      const n = Math.round(Number(text));
      if (!Number.isFinite(n) || n < 0 || n > max) {
        push("danger", `${label} illisible`, `Un nombre entier entre 0 et ${max}, ou vide.`);
        return false;
      }
      return n;
    };
    const maxAge = limit(draft.maxVehicleAgeYears, "Âge maximal", 50);
    if (maxAge === false) return;
    const maxKm = limit(draft.maxDistanceKm, "Rayon", 500);
    if (maxKm === false) return;
    if (maxKm === 0) {
      push("danger", "Rayon illisible", "Un rayon de transport se compte en kilomètres (1 à 500).");
      return;
    }
    const existingGroup = variants.find((v) => v.variantGroup)?.variantGroup ?? null;
    const plan: ServicePlan = {
      kind: "group",
      categoryId: draft.categoryId,
      variantGroup: single ? existingGroup : (existingGroup ?? newGroupKey()),
      shared: {
        name,
        description: draft.description.trim() || null,
        quoteOnly: draft.quoteOnly,
        onlineBookable: draft.onlineBookable,
        publicVisible: draft.publicVisible,
        aiPriceVisible: draft.aiPriceVisible,
        active: creating || draft.activeTouched ? draft.active : undefined,
        guideSlugs: draft.guideSlugs,
        bundleServiceIds: draft.bundleServiceIds,
        warrantyEligible: draft.warrantyEligible,
        vehicleClasses: draft.axis === "vehicle" ? [] : draft.vehicleClasses,
        pricingUnit: draft.pricingUnit,
        maxVehicleAgeYears: maxAge,
        maxDistanceKm: maxKm,
        links: draft.links,
      },
      rows,
      deleteIds: deletions.map((v) => v.id),
      products: items,
    };
    if (await onSave(plan)) onClose();
  };

  const excludedIds = new Set(variants.map((v) => v.id));
  const firstId = variants[0]?.id ?? null;
  // Une entrée par famille (le service, ou sa première déclinaison), hors celle éditée.
  const ownFamily = variants[0]?.variantGroup ?? null;
  const linkable = (() => {
    const seen = new Set<string>();
    const out: Service[] = [];
    for (const other of categories.flatMap((c) => c.services)) {
      if (!other.active || excludedIds.has(other.id)) continue;
      if (ownFamily && other.variantGroup === ownFamily) continue;
      const key = other.variantGroup ? `g:${other.variantGroup}` : `s:${other.id}`;
      // Une liaison existante désigne peut-être une autre déclinaison de la même famille.
      const linked = draft.links.find(
        (l) =>
          l.linkedServiceId === other.id ||
          (other.variantGroup &&
            categories.flatMap((c) => c.services).find((x) => x.id === l.linkedServiceId)
              ?.variantGroup === other.variantGroup),
      );
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(
        linked && linked.linkedServiceId !== other.id
          ? { ...other, id: linked.linkedServiceId }
          : other,
      );
    }
    return out;
  })();

  return (
    <Modal
      open
      title={group ? "Modifier le service" : "Nouveau service"}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annuler
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="primary"
            disabled={busy || !draft.name.trim()}
          >
            {group ? "Enregistrer" : "Créer le service"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="atelier-form" onSubmit={(e) => void submit(e)}>
        <div className="form-section__grid">
          <Field
            label="Nom"
            hint={single ? undefined : "Sans la déclinaison : elle s'ajoute au nom."}
          >
            <Input
              value={draft.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="ex : Antirouille annuel"
              autoFocus
              required
            />
          </Field>
          <Field label="Catégorie">
            <Select value={draft.categoryId} onChange={(e) => set("categoryId", e.target.value)}>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="form-section__grid form-section__grid--full">
          <Field label="Description">
            <Textarea
              className="textarea--sm"
              rows={2}
              value={draft.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </Field>
        </div>

        <fieldset className="atelier-subsection pricing">
          <legend className="atelier-subsection__title">Tarification</legend>
          <div className="pricing__axes" role="radiogroup" aria-label="Mode de tarification">
            {AXES.map((axis) => (
              <label key={axis} className={`pricing__axis${draft.axis === axis ? " is-on" : ""}`}>
                <input
                  type="radio"
                  name="pricing-axis"
                  value={axis}
                  checked={draft.axis === axis}
                  onChange={() => chooseAxis(axis)}
                />
                {AXIS_LABELS[axis]}
              </label>
            ))}
          </div>
          <p className="atelier-subsection__hint">
            {single
              ? "Le service a un prix et une durée."
              : draft.axis === "vehicle"
                ? "Un prix par type de véhicule : cochez les types offerts. Le bon tarif est proposé d'après la catégorie tarifaire du véhicule du client."
                : draft.axis === "custom"
                  ? "Chaque ligne devient une déclinaison au choix (« 30 min », « Avec sablage »…)."
                  : "Un prix par quantité ; « Ajout 1 » est le supplément par unité au-delà d'une quantité tarifée."}
          </p>
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={draft.quoteOnly}
              onChange={(e) => set("quoteOnly", e.target.checked)}
            />
            Sur devis (pas de prix fixe)
          </label>

          <div className={`pricing__grid${single ? " pricing__grid--single" : ""}`} role="table">
            <div className="pricing__head" role="row">
              {single ? null : <span role="columnheader">Déclinaison</span>}
              <span role="columnheader">Prix ($)</span>
              <span role="columnheader">Durée (min)</span>
              <span role="columnheader">Main-d'œuvre (min)</span>
              {single ? null : <span role="columnheader" />}
            </div>
            {draft.rows
              .filter((r) => single || r.fixed || r.included)
              .map((r) => (
                <div
                  key={r.key}
                  className={`pricing__row${r.included ? "" : " is-off"}`}
                  role="row"
                >
                  {single ? null : (
                    <span className="pricing__cell pricing__cell--label" role="cell">
                      {r.fixed ? (
                        <label className="pricing__pick">
                          <input
                            type="checkbox"
                            checked={r.included}
                            onChange={(e) => setRow(r.key, { included: e.target.checked })}
                          />
                          {r.vehicleClass ? (
                            <VehicleIcon
                              vehicleClass={r.vehicleClass}
                              width={34}
                              className="pricing__icon"
                            />
                          ) : null}
                          <span className="pricing__label">{r.label}</span>
                        </label>
                      ) : (
                        <Input
                          aria-label="Libellé de la déclinaison"
                          value={r.label}
                          onChange={(e) => setRow(r.key, { label: e.target.value })}
                          placeholder="ex : Avec sablage"
                        />
                      )}
                    </span>
                  )}
                  <span className="pricing__cell" role="cell">
                    <Input
                      aria-label={`Prix ${r.label || draft.name}`}
                      inputMode="decimal"
                      value={r.price}
                      onChange={(e) => setRow(r.key, { price: e.target.value })}
                      placeholder="0,00"
                      disabled={draft.quoteOnly || (!single && !r.included)}
                    />
                  </span>
                  <span className="pricing__cell" role="cell">
                    <Input
                      aria-label={`Durée ${r.label || draft.name}`}
                      type="number"
                      min={5}
                      step={5}
                      value={r.durationMin ?? ""}
                      placeholder="À préciser"
                      disabled={!single && !r.included}
                      onChange={(e) =>
                        setRow(r.key, {
                          durationMin: e.target.value === "" ? null : Number(e.target.value),
                        })
                      }
                    />
                  </span>
                  <span className="pricing__cell" role="cell">
                    <Input
                      aria-label={`Main-d'œuvre ${r.label || draft.name}`}
                      type="number"
                      min={0}
                      step={5}
                      value={r.laborMin}
                      placeholder={r.durationMin === null ? "À préciser" : String(r.durationMin)}
                      disabled={!single && !r.included}
                      onChange={(e) => setRow(r.key, { laborMin: e.target.value })}
                    />
                  </span>
                  {single ? null : (
                    <span className="pricing__cell pricing__cell--remove" role="cell">
                      {r.fixed ? null : (
                        <Button
                          variant="ghost-danger"
                          size="sm"
                          className="btn--icon"
                          aria-label={`Retirer ${r.label || "la déclinaison"}`}
                          title="Retirer"
                          icon={<Icon name="close" size={14} />}
                          onClick={() => removeRow(r.key)}
                        />
                      )}
                    </span>
                  )}
                </div>
              ))}
          </div>
          {draft.axis === "seats" || draft.axis === "carpets" ? (
            <div className="pricing__foot">
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="plus" size={14} />}
                onClick={addCountRow}
              >
                Ajouter une quantité
              </Button>
            </div>
          ) : draft.axis === "custom" ? (
            <div className="pricing__foot">
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="plus" size={14} />}
                onClick={addCustomRow}
              >
                Ajouter une déclinaison
              </Button>
            </div>
          ) : null}
          {deletions.length > 0 ? (
            <p className="pricing__warning" role="alert">
              À l'enregistrement,{" "}
              {deletions.length > 1
                ? "ces déclinaisons seront retirées"
                : "cette déclinaison sera retirée"}{" "}
              du catalogue : {deletions.map((v) => variantLabel(v) || v.name).join(", ")}. Les
              rendez-vous existants gardent leur libellé.
            </p>
          ) : null}
          <p className="atelier-subsection__hint">
            Main-d'œuvre : temps de technicien estimé pour le coût de revient ; vide = la durée.
          </p>
        </fieldset>

        <div className="atelier-checks">
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={draft.publicVisible}
              onChange={(e) => set("publicVisible", e.target.checked)}
            />
            Visible sur le site (tarif et demande)
          </label>
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={draft.onlineBookable}
              onChange={(e) => set("onlineBookable", e.target.checked)}
            />
            Réservation avec choix de créneau
          </label>
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={draft.aiPriceVisible}
              onChange={(e) => set("aiPriceVisible", e.target.checked)}
            />
            L'agent IA peut communiquer le prix
          </label>
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={(e) =>
                setDraft((d) => ({ ...d, active: e.target.checked, activeTouched: true }))
              }
            />
            Actif{!single && !creating ? " (toutes les déclinaisons)" : ""}
          </label>
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={draft.warrantyEligible}
              onChange={(e) => set("warrantyEligible", e.target.checked)}
            />
            Préparer la garantie à signer une heure après la fin du travail
          </label>
        </div>

        {draft.axis === "vehicle" ? null : (
          <fieldset className="atelier-subsection">
            <legend className="atelier-subsection__title">Catégories de véhicules</legend>
            <p className="atelier-subsection__hint">
              Aucune sélection : ce tarif concerne tous les véhicules (ou une option indépendante du
              véhicule).
            </p>
            <div className="atelier-checks">
              {VEHICLE_CLASSES.map((c) => (
                <label className="atelier-check" key={c}>
                  <input
                    type="checkbox"
                    checked={draft.vehicleClasses.includes(c)}
                    onChange={(e) =>
                      set(
                        "vehicleClasses",
                        e.target.checked
                          ? [...draft.vehicleClasses, c]
                          : draft.vehicleClasses.filter((x) => x !== c),
                      )
                    }
                  />
                  <VehicleIcon vehicleClass={c} width={30} className="pricing__icon" />
                  {VEHICLE_CLASS_LABELS[c]}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        <fieldset className="atelier-subsection">
          <legend className="atelier-subsection__title">Tarif et conditions</legend>
          <div className="atelier-checks">
            <label className="atelier-check">
              <input
                type="radio"
                name="pricing-unit"
                checked={draft.pricingUnit === "fixed"}
                onChange={() => set("pricingUnit", "fixed")}
              />
              Prix fixe
            </label>
            <label className="atelier-check">
              <input
                type="radio"
                name="pricing-unit"
                checked={draft.pricingUnit === "hourly"}
                onChange={() => set("pricingUnit", "hourly")}
              />
              Tarif horaire : le prix est le taux d’une heure, la durée sert d’estimation
            </label>
          </div>
          <div className="form-grid">
            <Field
              label="Âge maximal du véhicule (années)"
              hint="Vide : sans limite. Léo demande l’année avant de proposer ce service."
            >
              <Input
                type="number"
                min={0}
                max={50}
                value={draft.maxVehicleAgeYears}
                onChange={(e) => set("maxVehicleAgeYears", e.target.value)}
                placeholder="ex : 2"
              />
            </Field>
            <Field
              label="Rayon de transport (km)"
              hint="Pour un service de transport seulement : au-delà, il n’est pas offert."
            >
              <Input
                type="number"
                min={1}
                max={500}
                value={draft.maxDistanceKm}
                onChange={(e) => set("maxDistanceKm", e.target.value)}
                placeholder="ex : 10"
              />
            </Field>
          </div>
        </fieldset>

        <fieldset className="atelier-subsection">
          <legend className="atelier-subsection__title">Services liés</legend>
          <p className="atelier-subsection__hint">
            Obligatoire : ajouté d’office avec ce service (CRM, site, Léo). Recommandé : proposé,
            avec la raison, jamais ajouté sans accord.
          </p>
          <div className="service-links">
            {linkable.map((other) => {
              const current = draft.links.find((l) => l.linkedServiceId === other.id);
              const setLink = (kind: ServiceLinkItemInput["kind"] | "", note?: string | null) =>
                set(
                  "links",
                  kind === ""
                    ? draft.links.filter((l) => l.linkedServiceId !== other.id)
                    : current
                      ? draft.links.map((l) =>
                          l.linkedServiceId === other.id
                            ? { ...l, kind, note: note ?? l.note ?? null }
                            : l,
                        )
                      : [...draft.links, { linkedServiceId: other.id, kind, note: note ?? null }],
                );
              return (
                <div key={other.id} className={`service-link${current ? " is-on" : ""}`}>
                  <span className="service-link__name">{other.name.split(" — ")[0]}</span>
                  <Select
                    aria-label={`Liaison avec ${other.name}`}
                    value={current?.kind ?? ""}
                    onChange={(e) => setLink(e.target.value as ServiceLinkItemInput["kind"] | "")}
                  >
                    <option value="">—</option>
                    <option value="obligatoire">{SERVICE_LINK_KIND_LABELS.obligatoire}</option>
                    <option value="recommande">{SERVICE_LINK_KIND_LABELS.recommande}</option>
                  </Select>
                  {current ? (
                    <Input
                      aria-label={`Note pour ${other.name}`}
                      value={current.note ?? ""}
                      maxLength={300}
                      placeholder="Raison dite au client (facultatif)"
                      onChange={(e) => setLink(current.kind, e.target.value || null)}
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </fieldset>

        {creating || variants.length === 1 ? (
          <ServiceProductsEditor products={products} value={consumed} onChange={setConsumed} />
        ) : (
          <fieldset className="atelier-subsection">
            <legend className="atelier-subsection__title">Produits consommés</legend>
            <p className="atelier-subsection__empty">
              Propres à chaque déclinaison : cliquez sa pastille dans le catalogue pour les
              modifier. Une nouvelle déclinaison reprend ceux de la première.
            </p>
          </fieldset>
        )}

        <fieldset className="atelier-subsection">
          <legend className="atelier-subsection__title">Forfait : services inclus</legend>
          <p className="atelier-subsection__hint">
            Quand ces services sont cédulés séparément, l'écran propose ce forfait à la place s'il
            coûte moins cher.
          </p>
          <div className="service-guides__picker">
            {categories
              .flatMap((c) => c.services)
              .filter((other) => other.active && !excludedIds.has(other.id))
              .map((other) => (
                <label key={other.id} className="atelier-check">
                  <input
                    type="checkbox"
                    checked={draft.bundleServiceIds.includes(other.id)}
                    onChange={(e) =>
                      set(
                        "bundleServiceIds",
                        e.target.checked
                          ? [...draft.bundleServiceIds, other.id]
                          : draft.bundleServiceIds.filter((id) => id !== other.id),
                      )
                    }
                  />
                  {other.name}
                </label>
              ))}
          </div>
        </fieldset>

        <ServiceGuidesPicker
          value={draft.guideSlugs}
          onChange={(guideSlugs) => set("guideSlugs", guideSlugs)}
        />

        <FollowupsHint serviceId={firstId} />
      </form>
    </Modal>
  );
}

/** Rang d'une ligne de grille (quantités croissantes, appoint en dernier, libres à la fin). */
function rankOf(r: RowDraft): number {
  if (r.vehicleClass) return VEHICLE_CLASSES.indexOf(r.vehicleClass);
  if (r.count !== undefined) return r.extra ? 1000 + r.count : r.count;
  return 5000;
}

// ---- Une déclinaison seule ----

function VariantEditor({
  variant,
  group,
  products,
  busy,
  onClose,
  onSave,
}: ServiceEditorProps & { variant: Service; group: ServiceVariantGroup }) {
  const push = useToastStore((s) => s.push);
  const axis = variantAxis(group.variants);
  const parsed = parseServiceVariant(variant);
  const [label, setLabel] = useState(variantLabel(variant));
  const [vehicleClass, setVehicleClass] = useState<VehicleClass | "">(
    parsed.kind === "vehicle" && parsed.vehicleClasses.length === 1
      ? parsed.vehicleClasses[0]!
      : "",
  );
  const [price, setPrice] = useState(centsToInput(variant.priceCents));
  const [quoteOnly, setQuoteOnly] = useState(variant.quoteOnly);
  const [durationMin, setDurationMin] = useState<number | null>(variant.durationMin);
  const [laborMin, setLaborMin] = useState(laborToInput(variant.laborMin));
  const [active, setActive] = useState(variant.active);
  const [vehicleClasses, setVehicleClasses] = useState<VehicleClass[]>(
    variant.vehicleClasses ?? [],
  );
  const [consumed, setConsumed] = useState<DraftServiceProduct[]>([]);
  useEffect(() => {
    getServiceProducts(variant.id)
      .then((list) => setConsumed(list.map(draftFromServiceProduct)))
      .catch(() => setConsumed([]));
  }, [variant.id]);

  // Axe véhicule : la classe se choisit dans une liste et nomme la déclinaison.
  const byClass = axis === "vehicle" && (vehicleClass !== "" || parsed.kind !== "vehicle");
  const baseName = serviceBaseName(variant);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const finalLabel = byClass && vehicleClass ? VEHICLE_CLASS_LABELS[vehicleClass] : label.trim();
    if (!finalLabel) {
      push("danger", "Libellé manquant", "La déclinaison a besoin d'un libellé.");
      return;
    }
    const priceCents = quoteOnly ? null : parseAmountToCents(price);
    if (!quoteOnly && priceCents === null) {
      push("danger", "Prix illisible", "Saisissez un montant, par exemple 189,00.");
      return;
    }
    const laborRaw = laborMin.trim();
    const labor = laborRaw === "" ? null : Math.round(Number(laborRaw));
    if (labor !== null && (!Number.isFinite(labor) || labor < 0)) {
      push("danger", "Main-d'œuvre illisible", "Un nombre de minutes, ou vide.");
      return;
    }
    const items = serviceProductsToInput(consumed);
    if (!items) {
      push(
        "danger",
        "Produits incomplets",
        "Chaque ligne doit avoir un produit (sans doublon) et une quantité positive.",
      );
      return;
    }
    const plan: ServicePlan = {
      kind: "variant",
      id: variant.id,
      input: {
        name: `${baseName} — ${finalLabel}`,
        priceCents,
        quoteOnly,
        durationMin: durationMin === null ? null : Math.max(5, Math.round(durationMin) || 5),
        laborMin: labor,
        active,
        vehicleClasses: byClass && vehicleClass ? [vehicleClass] : vehicleClasses,
      },
      products: items,
    };
    if (await onSave(plan)) onClose();
  };

  return (
    <Modal
      open
      title={`${baseName} — déclinaison`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Annuler
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" disabled={busy}>
            Enregistrer
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="atelier-form" onSubmit={(e) => void submit(e)}>
        <p className="atelier-subsection__hint">
          Nom, description, visibilité, forfait et procédures se modifient sur le service lui-même
          (bouton « Modifier » de la rangée).
        </p>
        <div className="form-section__grid">
          {byClass ? (
            <Field label="Type de véhicule">
              <Select
                value={vehicleClass}
                onChange={(e) => setVehicleClass(e.target.value as VehicleClass | "")}
              >
                <option value="">Choisir…</option>
                {VEHICLE_CLASSES.map((c) => (
                  <option key={c} value={c}>
                    {VEHICLE_CLASS_LABELS[c]}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <Field label="Déclinaison" hint="Ajoutée au nom du service : « Nom — déclinaison ».">
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="ex : 2 tapis"
                autoFocus
                required
              />
            </Field>
          )}
          <Field
            label="Prix ($)"
            hint={quoteOnly ? "Ignoré : déclinaison sur devis." : "Prix fixe de cette déclinaison."}
          >
            <Input
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0,00"
              disabled={quoteOnly}
            />
          </Field>
          <Field label="Durée (minutes)">
            <Input
              type="number"
              min={5}
              step={5}
              value={durationMin ?? ""}
              placeholder="À préciser"
              onChange={(e) =>
                setDurationMin(e.target.value === "" ? null : Number(e.target.value))
              }
            />
          </Field>
          <Field
            label="Main-d'œuvre (minutes)"
            hint="Temps de technicien estimé pour le coût de revient. Vide : la durée."
          >
            <Input
              type="number"
              min={0}
              step={5}
              value={laborMin}
              onChange={(e) => setLaborMin(e.target.value)}
              placeholder={durationMin === null ? "À préciser" : String(durationMin)}
            />
          </Field>
        </div>
        <div className="atelier-checks">
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={quoteOnly}
              onChange={(e) => setQuoteOnly(e.target.checked)}
            />
            Sur devis (pas de prix fixe)
          </label>
          <label className="atelier-check">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Actif
          </label>
        </div>
        {byClass ? null : (
          <fieldset className="atelier-subsection">
            <legend className="atelier-subsection__title">Catégories de véhicules</legend>
            <p className="atelier-subsection__hint">
              Aucune sélection : cette déclinaison concerne tous les véhicules.
            </p>
            <div className="atelier-checks">
              {VEHICLE_CLASSES.map((c) => (
                <label className="atelier-check" key={c}>
                  <input
                    type="checkbox"
                    checked={vehicleClasses.includes(c)}
                    onChange={(e) =>
                      setVehicleClasses(
                        e.target.checked
                          ? [...vehicleClasses, c]
                          : vehicleClasses.filter((x) => x !== c),
                      )
                    }
                  />
                  <VehicleIcon vehicleClass={c} width={30} className="pricing__icon" />
                  {VEHICLE_CLASS_LABELS[c]}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <ServiceProductsEditor products={products} value={consumed} onChange={setConsumed} />
      </form>
    </Modal>
  );
}
